import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import ShareIcon from '@material-ui/icons/Share';
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import SvgIcon from '@material-ui/core/SvgIcon';
import { withStyles } from '@material-ui/core/styles';

import ScrollToTop from '../../utils/scroll-to-top';
import { shouldShowWebShare, webShareUrl } from '../../utils/web-share';
import getFacebookShareUrl from '../../utils/facebook-share';
import openLinkedInShare from '../../utils/linkedin-share';
import getBlueskyShareUrl from '../../utils/bluesky-share';
import shareToInstagramStory from '../../utils/instagram-story-share';
import { useUserDataContext } from '../../wiring/user-provider';

import Insta from '../../styles/social/instagram';
import Text from '../../styles/social/text';
import Facebook from '../../styles/social/facebook';
import LinkedIn from '../../styles/social/linkedin';
import Bluesky from '../../styles/social/bluesky';
import Logo from './images/logo_default.png';

import Styles from './styles';

import useI18n from '../../utils/use-i18n';
import ContentEnglish from './i18n/en';
import ContentSpanish from './i18n/es';

const content = {
    en: ContentEnglish,
    es: ContentSpanish,
};

const VolunteerActivismIcon = (props) => (
    // Since the VolunteerActivismIcon is not available in the current version of material-ui icons, we need to use a custom icon.
    <SvgIcon {...props}>
        <path d="M1 11h4v11H1zm15-7.75C16.65 2.49 17.66 2 18.7 2 20.55 2 22 3.45 22 5.3c0 2.27-2.91 4.9-6 7.7-3.09-2.81-6-5.44-6-7.7C10 3.45 11.45 2 13.3 2c1.04 0 2.05.49 2.7 1.25zM20 17h-7l-2.09-.73.33-.94L13 16h2.82c.65 0 1.18-.53 1.18-1.18 0-.49-.31-.93-.77-1.11L8.97 11H7v9.02L14 22l8.01-3c-.01-1.1-.9-2-2.01-2z" />
    </SvgIcon>
);

const Header = ({ classes }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const { selectedState, selectedLanguage } = useUserDataContext();
    const history = useHistory();

    const showTextShare = shouldShowWebShare();

    const reset = () => {
        history.push('/');
        ScrollToTop();
    };

    return (
        <>
            <header className={classes.wrapper}>
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col">
                            <button
                                type="button"
                                onClick={reset}
                                aria-label="Clear Selected State">
                                <img
                                    src={Logo}
                                    alt=""
                                    className={classes.logoImg}
                                />
                            </button>
                        </div>
                        <div className="col d-flex justify-content-end">
                            {/* Removed language toggle buttons for now since we only have one language for the updated state content. If we need to add more languages, we can add them back in. */}
                            <button
                                type="button"
                                className={classes.shareBtn}
                                onClick={(e) => {
                                    e.currentTarget.blur(); // blur the button to remove the focus outline
                                    setModalOpen(true);
                                }}>
                                <span className={classes.shareIcon}>
                                    <ShareIcon />
                                </span>
                                <span className={classes.shareTxt}>
                                    {useI18n('share_label_1', content)}
                                    <br />
                                    {useI18n('share_label_2', content)}
                                </span>
                            </button>
                            <button
                                type="button"
                                className={classes.donateBtn}
                                onClick={() =>
                                    window.open(
                                        'https://nationalcivicleague.app.neoncrm.com/campaigns/read-the-f-directions',
                                        '_blank',
                                    )
                                }>
                                <span className={classes.donateIcon}>
                                    <VolunteerActivismIcon />
                                </span>
                                <span className={classes.donateTxt}>
                                    {useI18n('donate_label', content)}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                aria-labelledby="share-modal-title"
                aria-describedby="share-modal-description"
                BackdropProps={{
                    className: classes.modalRoot,
                }}>
                <div className={`container ${classes.modalWrapper}`}>
                    <p id="share-modal-description" className="offscreen">
                        Share this site with friends using the options listed
                        here.
                    </p>
                    <div
                        className="row justify-space-between pt-4"
                        style={{ paddingBottom: 140 }}>
                        <div className="col pr-0">
                            <span
                                id="share-modal-title"
                                className={`d-sm-block d-none ${classes.modalTitle}`}>
                                Tell Your Friends
                                <br />
                                Not To{' '}
                                <span className={classes.modalTitleHighlight}>
                                    F*
                                </span>{' '}
                                This Up!
                            </span>
                            <span
                                id="share-modal-title"
                                className={`d-sm-none d-block ${classes.modalTitle}`}>
                                Tell Your
                                <br />
                                Friends Not
                                <br />
                                To{' '}
                                <span className={classes.modalTitleHighlight}>
                                    F*
                                </span>{' '}
                                This Up!
                            </span>
                        </div>
                        <div className="col-auto d-flex justify-content-end align-items-baseline pl-0">
                            <IconButton
                                color="inherit"
                                aria-label="Close share modal"
                                onClick={() => setModalOpen(false)}
                                edge="end"
                                className={classes.modalCloseBtn}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col text-center">
                            <button
                                type="button"
                                className={classes.socialBlock}
                                onClick={shareToInstagramStory}
                                aria-label="Share to Instagram Story">
                                <Insta />
                            </button>
                            {showTextShare && (
                                <button
                                    type="button"
                                    className={classes.socialBlock}
                                    onClick={webShareUrl}
                                    aria-label="Share via Text Message">
                                    <Text />
                                </button>
                            )}
                            <a
                                className={classes.socialBlock}
                                href={getFacebookShareUrl(
                                    selectedLanguage,
                                    selectedState,
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Share on Facebook">
                                <Facebook />
                            </a>
                            <button
                                type="button"
                                className={classes.socialBlock}
                                onClick={() =>
                                    openLinkedInShare(
                                        selectedLanguage,
                                        selectedState,
                                    )
                                }
                                aria-label="Share on LinkedIn">
                                <LinkedIn />
                            </button>
                            <a
                                className={classes.socialBlock}
                                href={getBlueskyShareUrl(
                                    selectedLanguage,
                                    selectedState,
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Share on Bluesky">
                                <Bluesky />
                            </a>
                        </div>
                    </div>
                    {isMobile && (
                        <div className="row">
                            <div className="col text-center">
                                <Typography
                                    variant="caption"
                                    component="p"
                                    className={classes.instaHint}>
                                    Sharing to Instagram? Choose it from the
                                    menu that pops up next.
                                </Typography>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

Header.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(Styles)(Header);
